// Collision detection + automatic resolution.
// Siblings (items sharing a parent) must have unique final names.

import { splitExt, joinExt } from "@/lib/rename/ops";

export const CONFLICT = "conflict"; // exact duplicate destination
export const CASE = "case"; // case-only difference (risky on macOS)

function finalName(row) {
  return row.include ? row.suggested : row.original;
}

// Returns { statusById: {id: 'conflict'|'case'}, hasConflicts, count }
export function detectCollisions(rows) {
  const groups = new Map();
  for (const r of rows) {
    const key = `${r.rootId || ""}|${r.parent || ""}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const statusById = {};
  for (const [, group] of groups) {
    const byExact = new Map();
    const byLower = new Map();
    for (const r of group) {
      const n = finalName(r);
      (byExact.get(n) || byExact.set(n, []).get(n)).push(r);
      const l = n.toLowerCase();
      (byLower.get(l) || byLower.set(l, []).get(l)).push(r);
    }
    for (const [, rs] of byExact) {
      if (rs.length > 1) rs.forEach((r) => { if (r.include) statusById[r.id] = CONFLICT; });
    }
    for (const [, rs] of byLower) {
      if (rs.length > 1) {
        const exacts = new Set(rs.map(finalName));
        if (exacts.size > 1)
          rs.forEach((r) => {
            if (r.include && !statusById[r.id]) statusById[r.id] = CASE;
          });
      }
    }
  }
  const count = Object.values(statusById).filter((v) => v === CONFLICT).length;
  return { statusById, hasConflicts: count > 0, count };
}

// Produce overrides {id: newName} that disambiguate colliding included rows.
// style: "space" -> "Name 2", "paren" -> "Name (1)"
export function resolveConflicts(rows, style = "space") {
  const groups = new Map();
  for (const r of rows) {
    const key = `${r.rootId || ""}|${r.parent || ""}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  const overrides = {};
  for (const [, group] of groups) {
    const used = new Set();
    // reserve names of items that are not being renamed / excluded first
    group.forEach((r) => {
      if (!r.include) used.add(finalName(r).toLowerCase());
    });
    group
      .filter((r) => r.include)
      .forEach((r) => {
        const [base, ext] = splitExt(r.suggested, r.type);
        let candidate = r.suggested;
        let k = 1;
        while (used.has(candidate.toLowerCase())) {
          const tag = style === "paren" ? ` (${k})` : ` ${k + 1}`;
          candidate = joinExt(`${base}${tag}`, ext);
          k += 1;
        }
        if (candidate !== r.suggested) overrides[r.id] = candidate;
        used.add(candidate.toLowerCase());
      });
  }
  return overrides;
}
